import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import type { ElectricalSpecification } from './specification';
import type { Line } from './types';
import { useDraggableDialog } from './useDraggableDialog';

interface SpecificationDialogProps {
  specification: ElectricalSpecification;
  lines: Line[];
  onClose: () => void;
}

interface CountRow {
  key: string;
  label: string;
  value: number;
}

type CsvCell = string | number;
type CsvRow = CsvCell[];

function isEmptyRecord(record: Record<string | number, number>): boolean {
  return Object.keys(record).length === 0;
}

function escapeCsvCell(value: CsvCell): string {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function buildCsv(rows: CsvRow[]): string {
  return rows.map(row => row.map(escapeCsvCell).join(',')).join('\n');
}

function downloadTextFile(content: string, filename: string, contentType: string): void {
  const blob = new Blob([content], { type: contentType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function sortStringCountEntries(record: Record<string, number>): Array<[string, number]> {
  return Object.entries(record).sort(([a], [b]) => a.localeCompare(b));
}

function sortPostCountEntries(record: Record<number, number>): Array<[number, number]> {
  return Object.entries(record)
    .map(([posts, count]) => [Number(posts), count] as [number, number])
    .sort(([a], [b]) => a - b);
}

function CountList({ rows, emptyLabel }: { rows: CountRow[]; emptyLabel: string }) {
  if (rows.length === 0) {
    return <div className="rounded-md border border-border/60 bg-surface px-3 py-2 text-xs text-ink-4">{emptyLabel}</div>;
  }

  return (
    <div className="divide-y divide-border/60 overflow-hidden rounded-md border border-border/60 bg-surface">
      {rows.map(row => (
        <div key={row.key} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
          <span className="min-w-0 truncate text-ink-3">{row.label}</span>
          <span className="font-mono font-medium text-ink">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-md border border-border/60 bg-surface-2 p-3">
      <h3 className="mb-2 text-[11px] font-medium uppercase text-ink-4">{title}</h3>
      {children}
    </section>
  );
}

export function SpecificationDialog({ specification, lines, onClose }: SpecificationDialogProps) {
  const { t } = useTranslation();
  const { overlayRef, panelRef, panelStyle, dragHandleProps } = useDraggableDialog(true);

  const lineNameById = useMemo(() => new Map(lines.map(line => [line.id, line.name])), [lines]);
  const itemLabel = (type: string) => t(`floor_plan.item_${type}`, { defaultValue: type });
  const postsLabel = (posts: number) => t('floor_plan.spec_posts', { count: posts });

  const powerRows = sortStringCountEntries(specification.powerPoints.byType).map(([type, count]) => ({
    key: type,
    label: itemLabel(type),
    value: count,
  }));
  const excludedRows = sortStringCountEntries(specification.powerPoints.excludedByType).map(([type, count]) => ({
    key: type,
    label: itemLabel(type),
    value: count,
  }));
  const weakCurrentRows = sortStringCountEntries(specification.weakCurrent.byType).map(([type, count]) => ({
    key: type,
    label: itemLabel(type),
    value: count,
  }));
  const frameRows = sortPostCountEntries(specification.frames.byPosts).map(([posts, count]) => ({
    key: String(posts),
    label: postsLabel(posts),
    value: count,
  }));
  const drywallRows = sortPostCountEntries(specification.boxes.drywall.byPosts).map(([posts, count]) => ({
    key: String(posts),
    label: postsLabel(posts),
    value: count,
  }));
  const switchLineRows = Object.entries(specification.switches.byLine).sort(([a], [b]) => a.localeCompare(b));
  const isEmptyPlan = specification.powerPoints.total === 0
    && isEmptyRecord(specification.powerPoints.excludedByType)
    && specification.frames.total === 0
    && specification.boxes.brickConcrete.totalMechanisms === 0
    && specification.switches.totals.interruptores === 0
    && specification.switches.totals.conmutadores === 0
    && specification.switches.totals.cruzamientos === 0;

  const handleDownloadCsv = () => {
    const countLabel = t('floor_plan.spec_count');
    const rows: CsvRow[] = [
      [t('floor_plan.specification')],
      [],
      [t('floor_plan.spec_power_points')],
      [t('floor_plan.spec_total'), specification.powerPoints.total],
      [t('floor_plan.spec_by_type'), countLabel],
      ...powerRows.map(row => [row.label, row.value] as CsvRow),
      [],
      [t('floor_plan.spec_excluded'), countLabel],
      ...excludedRows.map(row => [row.label, row.value] as CsvRow),
      [],
      [t('floor_plan.spec_weak_current'), countLabel],
      ...weakCurrentRows.map(row => [row.label, row.value] as CsvRow),
      [],
      [t('floor_plan.spec_switches')],
      [t('floor_plan.spec_interruptores'), specification.switches.totals.interruptores],
      [t('floor_plan.spec_conmutadores'), specification.switches.totals.conmutadores],
      [t('floor_plan.spec_cruzamientos'), specification.switches.totals.cruzamientos],
      [],
      [
        t('floor_plan.spec_line'),
        t('floor_plan.spec_mechanisms'),
        t('floor_plan.spec_interruptores'),
        t('floor_plan.spec_conmutadores'),
        t('floor_plan.spec_cruzamientos'),
      ],
      ...switchLineRows.map(([lineId, row]) => [
        lineNameById.get(lineId) ?? lineId,
        row.mechanisms,
        row.interruptores,
        row.conmutadores,
        row.cruzamientos,
      ] as CsvRow),
      [],
      [t('floor_plan.spec_frames')],
      [t('floor_plan.spec_total'), specification.frames.total],
      [t('floor_plan.spec_posts', { count: 0 }), countLabel],
      ...frameRows.map(row => [row.label, row.value] as CsvRow),
      [],
      [t('floor_plan.spec_boxes')],
      [t('floor_plan.spec_brick_concrete'), specification.boxes.brickConcrete.totalMechanisms],
      [t('floor_plan.spec_drywall'), countLabel],
      ...drywallRows.map(row => [row.label, row.value] as CsvRow),
      [],
      [t('floor_plan.spec_warnings')],
      [t('floor_plan.spec_warning_code'), t('floor_plan.spec_line'), t('floor_plan.spec_warning_message')],
      ...specification.warnings.map(warning => [
        warning.code,
        warning.lineId ? lineNameById.get(warning.lineId) ?? warning.lineId : '',
        warning.message,
      ] as CsvRow),
    ];

    downloadTextFile(`\uFEFF${buildCsv(rows)}\n`, 'panelforge-specification.csv', 'text/csv;charset=utf-8');
  };

  return (
    <div ref={overlayRef} className="absolute inset-0 z-20 bg-black/35">
      <div
        ref={panelRef}
        style={panelStyle}
        className="absolute flex max-h-[min(42rem,calc(100vh-1.5rem))] w-[min(46rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-lg border border-border bg-surface text-ink shadow-xl"
      >
        <div
          className="cursor-grab border-b border-border bg-surface-2 px-4 py-3 active:cursor-grabbing"
          {...dragHandleProps}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-ink">{t('floor_plan.specification')}</div>
              <div className="mt-1 text-xs text-ink-4">
                {isEmptyPlan ? t('floor_plan.spec_empty') : t('floor_plan.spec_total')}
              </div>
            </div>
            <div className="mt-0.5 flex gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {isEmptyPlan && (
            <div className="rounded-md border border-border/60 bg-surface-2 px-3 py-2 text-xs text-ink-4">
              {t('floor_plan.spec_empty')}
            </div>
          )}

          <Section title={t('floor_plan.spec_power_points')}>
            <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-border/60 bg-surface px-3 py-2 text-xs">
              <span className="text-ink-3">{t('floor_plan.spec_total')}</span>
              <span className="font-mono font-medium text-ink">{specification.powerPoints.total}</span>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="mb-1.5 text-[11px] font-medium text-ink-4">{t('floor_plan.spec_by_type')}</div>
                <CountList rows={powerRows} emptyLabel={t('floor_plan.spec_none')} />
              </div>
              <div>
                <div className="mb-1.5 text-[11px] font-medium text-ink-4">{t('floor_plan.spec_excluded')}</div>
                <CountList rows={excludedRows} emptyLabel={t('floor_plan.spec_none')} />
              </div>
            </div>
            <div className="mt-3">
              <div className="mb-1.5 text-[11px] font-medium text-ink-4">{t('floor_plan.spec_weak_current')}</div>
              <CountList rows={weakCurrentRows} emptyLabel={t('floor_plan.spec_none')} />
            </div>
          </Section>

          <Section title={t('floor_plan.spec_switches')}>
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-md border border-border/60 bg-surface px-3 py-2">
                <div className="text-[11px] text-ink-4">{t('floor_plan.spec_interruptores')}</div>
                <div className="font-mono text-sm font-semibold text-ink">{specification.switches.totals.interruptores}</div>
              </div>
              <div className="rounded-md border border-border/60 bg-surface px-3 py-2">
                <div className="text-[11px] text-ink-4">{t('floor_plan.spec_conmutadores')}</div>
                <div className="font-mono text-sm font-semibold text-ink">{specification.switches.totals.conmutadores}</div>
              </div>
              <div className="rounded-md border border-border/60 bg-surface px-3 py-2">
                <div className="text-[11px] text-ink-4">{t('floor_plan.spec_cruzamientos')}</div>
                <div className="font-mono text-sm font-semibold text-ink">{specification.switches.totals.cruzamientos}</div>
              </div>
            </div>

            <div className="mt-3">
              <div className="mb-1.5 text-[11px] font-medium text-ink-4">{t('floor_plan.spec_by_line')}</div>
              {switchLineRows.length === 0 ? (
                <div className="rounded-md border border-border/60 bg-surface px-3 py-2 text-xs text-ink-4">{t('floor_plan.spec_none')}</div>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border/60 bg-surface">
                  <table className="min-w-full text-left text-xs">
                    <thead className="border-b border-border/60 bg-surface-2 text-[11px] uppercase text-ink-4">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t('floor_plan.spec_line')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('floor_plan.spec_mechanisms')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('floor_plan.spec_interruptores')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('floor_plan.spec_conmutadores')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('floor_plan.spec_cruzamientos')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {switchLineRows.map(([lineId, row]) => (
                        <tr key={lineId}>
                          <td className="px-3 py-2 text-ink-3">{lineNameById.get(lineId) ?? lineId}</td>
                          <td className="px-3 py-2 text-right font-mono text-ink">{row.mechanisms}</td>
                          <td className="px-3 py-2 text-right font-mono text-ink">{row.interruptores}</td>
                          <td className="px-3 py-2 text-right font-mono text-ink">{row.conmutadores}</td>
                          <td className="px-3 py-2 text-right font-mono text-ink">{row.cruzamientos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Section>

          <Section title={t('floor_plan.spec_frames')}>
            <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-border/60 bg-surface px-3 py-2 text-xs">
              <span className="text-ink-3">{t('floor_plan.spec_total')}</span>
              <span className="font-mono font-medium text-ink">{specification.frames.total}</span>
            </div>
            <CountList rows={frameRows} emptyLabel={t('floor_plan.spec_none')} />
          </Section>

          <Section title={t('floor_plan.spec_boxes')}>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="mb-1.5 text-[11px] font-medium text-ink-4">{t('floor_plan.spec_brick_concrete')}</div>
                <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-surface px-3 py-2 text-xs">
                  <span className="text-ink-3">{t('floor_plan.spec_mechanisms')}</span>
                  <span className="font-mono font-medium text-ink">{specification.boxes.brickConcrete.totalMechanisms}</span>
                </div>
              </div>
              <div>
                <div className="mb-1.5 text-[11px] font-medium text-ink-4">{t('floor_plan.spec_drywall')}</div>
                <CountList rows={drywallRows} emptyLabel={t('floor_plan.spec_none')} />
              </div>
            </div>
          </Section>

          <Section title={t('floor_plan.spec_warnings')}>
            {specification.warnings.length === 0 ? (
              <div className="rounded-md border border-border/60 bg-surface px-3 py-2 text-xs text-ink-4">{t('floor_plan.spec_none')}</div>
            ) : (
              <div className="space-y-2">
                {specification.warnings.map((warning, index) => (
                  <div key={`${warning.code}-${warning.lineId ?? 'none'}-${index}`} className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                    {warning.message}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-surface-2 px-4 py-3">
          <Button type="button" size="sm" onClick={handleDownloadCsv}>
            {t('floor_plan.spec_download_csv')}
          </Button>
          <Button type="button" size="sm" onClick={onClose}>
            {t('floor_plan.spec_close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
